/**
 * RBAC permission matrix for Krix.
 *
 * Resources and actions follow Section 6 of claude-instructions.md.
 * Encoded once here; consumed by apps/api (requirePermission middleware)
 * and apps/admin (UI gates).
 */

import { createAccessControl } from "better-auth/plugins/access";
import { defaultStatements, adminAc, userAc } from "better-auth/plugins/admin/access";

/** Krix-specific statements layered onto Better-Auth's built-ins. */
export const statements = {
  ...defaultStatements,
  series: ["create", "read", "update", "publish", "delete"],
  episode: ["create", "update", "delete"],
  subscription: ["read", "refund", "comp", "cancel"],
  audit: ["read"],
  settings: ["read", "update"],
  search: ["rebuild"],
} as const;

export const ac = createAccessControl(statements);

/** Default `user`: read own data, manage own session, nothing else. */
export const user = ac.newRole({
  ...userAc.statements,
  series: ["read"],
});

/**
 * `support`: read user data + refund power, ban toggling (ban duration = 0 = unban),
 * no content edits.
 */
export const support = ac.newRole({
  user: ["list", "ban"],
  session: ["list", "revoke"],
  series: ["read"],
  subscription: ["read", "refund"],
  audit: ["read"],
});

/** `content_manager`: full CRUD on catalog, no user / billing / settings access. */
export const content_manager = ac.newRole({
  series: ["create", "read", "update", "publish", "delete"],
  episode: ["create", "update", "delete"],
  search: ["rebuild"],
  audit: ["read"],
});

/** `admin`: superset of support + content_manager + subscription management. */
export const admin = ac.newRole({
  ...adminAc.statements,
  series: ["create", "read", "update", "publish", "delete"],
  episode: ["create", "update", "delete"],
  subscription: ["read", "refund", "comp", "cancel"],
  audit: ["read"],
  settings: ["read", "update"],
  search: ["rebuild"],
});

/**
 * `superadmin`: every action on every resource.
 * Manages other admins; can impersonate any user.
 */
export const superadmin = ac.newRole({
  user: [
    "list",
    "create",
    "set-role",
    "ban",
    "impersonate",
    "impersonate-admins",
    "delete",
    "set-password",
    "get",
    "update",
  ],
  session: ["list", "revoke", "delete"],
  series: ["create", "read", "update", "publish", "delete"],
  episode: ["create", "update", "delete"],
  subscription: ["read", "refund", "comp", "cancel"],
  audit: ["read"],
  settings: ["read", "update"],
  search: ["rebuild"],
});

export const roles = {
  user,
  support,
  content_manager,
  admin,
  superadmin,
};
